/**
 * Deflection causes — the ONE definition (ENG-10054).
 *
 * WHY THIS LIVES IN CORE. This list had three copies: the writer
 * (`packages/mcp/src/ack-reaction.ts`), the probe reader
 * (`apps/cli/src/lib/responsiveness-probe.ts`), and the API ingest filter
 * (`packages/api/src/routes/host-runtime.ts`). The third had drifted to TEN of
 * the twelve, missing `aged_out_unrecoverable` and `marker_corrupt`, and the
 * ingest drops an unlisted cause with a bare `continue` — no log, no counter —
 * before `PutMetricData`.
 *
 * So those two CloudWatch series read zero, and the zero meant "never
 * ingested", not "never happened". They are ENG-9414's message-DESTRUCTION
 * counters, added precisely because "four destroyed customer inbounds in one
 * hour produced zero alerts". The counters shipped; the metric never arrived;
 * the silence they were built to end continued. A drifted copy of a list is
 * how a fix gets quietly undone.
 *
 * The ingest filter itself was right to be an allowlist — its comment explains
 * that an unknown key from a corrupt counter file would otherwise add an
 * unbounded CloudWatch dimension value. That reasoning is preserved: this is
 * still a closed allowlist, it is simply the SAME allowlist everywhere.
 *
 * RULE FOR WHOEVER ADDS THE NEXT CAUSE: add it here and nowhere else. If you
 * find yourself typing a cause string into a second file, that is the defect
 * this module exists to prevent.
 */

/**
 * Why an inbound could not be delivered at all. The three hard-failure classes
 * `classifyUndeliverableCause` attributes; everything else is a healthy path.
 *
 * Moved here with `DeflectionCause` (ENG-10054) because the latter is a union
 * over it, and splitting a type across packages to keep a constant in one place
 * would just trade one drift surface for another. `ack-reaction.ts` re-exports
 * both, so existing importers are unaffected.
 */
export type UndeliverableCause = 'integration_down' | 'session_dead' | 'wedged';

/**
 * The attributed trigger for a deflection. The undeliverable subset comes
 * straight from classifyUndeliverableCause; 'busy' is the busy-ack path;
 * 'duplicate' is a redundant re-delivery dropped at fresh ingress (ENG-6270);
 * 'replay_orphaned' is a recoverable inbound (a marker carrying a replay
 * payload) the orphan sweep GC'd WITHOUT it ever being replayed (ENG-6355 —
 * the candidate-silent-loss canary); 'replay_exhausted' (ENG-7736) is its
 * sibling for a marker GC'd AFTER one or more replay pushes that still never
 * cleared - under respawn churn a forced push can land in a session that is
 * killed before the turn starts, and without this cause those losses would be
 * invisible (replay_orphaned requires replay_count === 0, so bumping the
 * count would otherwise silence the alarm that caught the Sherlock loss); and
 * 'unknown' is the fail-safe when the cause genuinely can't be determined
 * (un-probeable host whose liveness reads 'unknown') — honest
 * under-attribution rather than a confident wrong label.
 *
 * ENG-6270/6355: 'duplicate' and 'replay_orphaned' share this cause-dimensioned
 * pipeline (rather than sibling metrics) so the ChannelDeflections{Cause=…}
 * series give the pre-flip baselines the ENG-6354 replay soak watches — dup-rate
 * (Cause=duplicate) must stay ~0 and candidate-silent-loss (Cause=replay_orphaned)
 * must fall when replay is enabled. No new counter-file kind, probe field, or
 * PutMetricData path to maintain.
 */
export type DeflectionCause =
  | UndeliverableCause
  | 'busy'
  | 'duplicate'
  | 'duplicate_mention'
  // ENG-8387 AC3: an echo we dropped whose app_mention twin NEVER arrived -
  // a mention thrown away and never delivered. Distinct from duplicate_mention,
  // which is a mention that did arrive and was dropped as a re-delivery.
  | 'mention_echo_orphan'
  | 'replay_orphaned'
  | 'replay_exhausted'
  // ENG-9414: a genuinely-aged, non-discretionary marker GC'd with NO replay
  // payload. `replay_orphaned` cannot see this class, by construction: it
  // requires `marker.payload`, and this is precisely the marker that has none.
  //
  // It is not the mild case. The payload is attached ~180 lines and three
  // awaits AFTER the marker is written (slack-channel.ts:9519 vs :9701), and
  // the in-session push to the agent happens after the attach - so a throw in
  // that window leaves a marker with no payload whose message THE AGENT WAS
  // NEVER NOTIFIED OF AT ALL. Unreplayable and undelivered. Under the old gate
  // it was deleted at 30 minutes with no record anywhere that it had existed.
  | 'aged_out_unrecoverable'
  // ENG-9414: a marker deleted WITHOUT being aged - structurally broken
  // (missing channel/thread/message ts), unparseable, or carrying a
  // future-dated `received_at`. `isPendingMarkerStale` deletes all three;
  // `isMarkerGenuinelyAged` returns false for all three, so none was counted.
  //
  // The clock-skew case is the one to watch: an NTP step backwards
  // future-dates every marker written in that window, so a single skew event
  // deletes an agent's entire live queue silently. A non-zero rate here means
  // messages are being destroyed for a reason that has nothing to do with age.
  | 'marker_corrupt'
  | 'unknown';

export const DEFLECTION_CAUSES: readonly DeflectionCause[] = [
  'integration_down',
  'session_dead',
  'wedged',
  'busy',
  'duplicate',
  // ENG-8387: an `app_mention` — the CANONICAL delivery of an @mention — dropped
  // by the fresh-ingress dedup. Split out of the generic `duplicate` bucket
  // because the two mean very different things: `duplicate` is dominated by the
  // deliberate, high-volume ENG-6378 echo drop and by routine reconnect
  // redelivery, both benign, while a dropped app_mention has the exact shape of
  // a lost mention. Conflated, the second is invisible inside the first — which
  // is how ENG-6378's silent loss ran unobserved.
  'duplicate_mention',
  'mention_echo_orphan',
  'replay_orphaned',
  'replay_exhausted',
  // ENG-9414 — see the type union above for why these two exist. Both are
  // message-loss classes the pre-existing causes were structurally unable to
  // observe, which is why four destroyed customer inbounds in one hour
  // produced zero alerts.
  'aged_out_unrecoverable',
  'marker_corrupt',
  'unknown',
];

/**
 * The same causes as a Set, for the membership tests the readers do.
 *
 * Typed `ReadonlySet<string>` rather than `ReadonlySet<DeflectionCause>` on
 * purpose: every caller is checking an UNTRUSTED string off the wire (a probe
 * payload, a counter file) against the allowlist. A `Set<DeflectionCause>`
 * would force each of them to cast the untrusted value to the very type the
 * check exists to establish, which defeats the check.
 */
export const DEFLECTION_CAUSE_SET: ReadonlySet<string> = new Set<string>(DEFLECTION_CAUSES);

/** Narrowing membership test over the one allowlist. */
export function isDeflectionCause(value: string): value is DeflectionCause {
  return DEFLECTION_CAUSE_SET.has(value);
}
